import React, { useState } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { AuthState } from '../types';
import { signIn, completeNewPassword } from '../lib/cognito';

interface LoginPageProps {
  onLogin: (auth: AuthState & { username?: string }) => void;
}

type Screen = 'login' | 'newPassword';

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  // ── Login fields ──────────────────────────────────────────────────────────
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // ── New-password-required challenge ──────────────────────────────────────
  const [screen, setScreen]               = useState<Screen>('login');
  const [pendingUser, setPendingUser]     = useState<CognitoUser | null>(null);
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  // ── Login submit ──────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn(username.trim(), password);

      if (result.type === 'newPasswordRequired') {
        setPendingUser(result.cognitoUser);
        setScreen('newPassword');
      } else {
        onLogin({
          isAuthenticated: true,
          restaurantId:   result.restaurantId,
          restaurantName: result.restaurantName,
          role:           result.role,
          username: username.trim(),
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(msg === 'Incorrect username or password.' ? 'Invalid username or password.' : msg);
    } finally {
      setLoading(false);
    }
  };

  // ── New-password submit ───────────────────────────────────────────────────
  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      const result = await completeNewPassword(pendingUser!, newPassword);
      onLogin({
        isAuthenticated: true,
        restaurantId:   result.restaurantId,
        restaurantName: result.restaurantName,
        role:           result.role,
        username: username.trim(),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to set password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Shared background ─────────────────────────────────────────────────────
  const Bg = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/kiosk_placeholder.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          animation: 'kitchenZoom 25s ease-in-out infinite alternate',
        }}
      />
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} />
      <style>{`@keyframes kitchenZoom { from { transform: scale(1); } to { transform: scale(1.12); } }`}</style>
    </div>
  );

  const inputCls =
    'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 text-sm transition-colors';

  const btnCls =
    'w-full py-3.5 rounded-xl font-bold text-sm bg-brand-500 hover:bg-brand-400 text-white transition-all duration-200 shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 disabled:opacity-60 mt-2';

  // ── New-password screen ───────────────────────────────────────────────────
  if (screen === 'newPassword') {
    return (
      <div className="min-h-screen bg-surface-900 flex items-center justify-center px-4">
        <Bg />
        <div className="relative w-full max-w-sm">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-500 font-black text-white text-2xl shadow-2xl shadow-brand-500/40 mb-4">
              K
            </div>
            <h1 className="text-white text-2xl font-black">Set Your Password</h1>
            <p className="text-gray-500 text-sm mt-1">Choose a permanent password for your account</p>
          </div>

          <div className="bg-surface-700/60 backdrop-blur-xl border border-white/8 rounded-2xl p-8 shadow-2xl">
            <form onSubmit={handleSetNewPassword} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  autoComplete="new-password"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  required
                  autoComplete="new-password"
                  className={inputCls}
                />
              </div>

              {error && (
                <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <button type="submit" disabled={loading} className={btnCls}>
                {loading ? '⏳ Saving...' : 'Set Password & Sign In →'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Login screen ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-900 flex items-center justify-center px-4">
      <Bg />
      <div className="relative w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-500 font-black text-white text-2xl shadow-2xl shadow-brand-500/40 mb-4">
            K
          </div>
          <h1 className="text-white text-2xl font-black">KioskFlow</h1>
          <p className="text-gray-500 text-sm mt-1">Kitchen Dashboard</p>
        </div>

        <div className="bg-surface-700/60 backdrop-blur-xl border border-white/8 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-white font-bold text-lg mb-6">Sign In</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your username"
                required
                autoComplete="username"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className={inputCls}
              />
            </div>

            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading} className={btnCls}>
              {loading ? '⏳ Signing in...' : 'Sign In to Kitchen'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/5">
            <p className="text-center text-gray-600 text-xs">
              Contact AccTech to get your login credentials
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
